import { useCallback, useState } from 'react'
import { getAttribution } from '../../helpers/leadAttribution'
import { buildLeadSignaturePayload } from '../../helpers/mhlLeadSignature'

export type Title = 'Mr' | 'Mrs' | 'Miss' | 'Ms' | 'Mx' | 'Dr' | 'Prof' | 'Sir' | 'Dame' | 'Rev' | 'Capt' | 'Major' | 'Lt' | 'Col' | 'General' | 'Other' | ''

export const SERVICE_BRANCHES = [
    'Army',
    'Royal Navy',
    'RAF',
    'Army Reserve',
    'Royal Navy Reserve',
    'RAF Auxiliary',
] as const

export type ServiceBranch = (typeof SERVICE_BRANCHES)[number] | ''

export interface ServiceRecord {
    id: string
    serviceNumber: string
    branch: ServiceBranch
    enlistmentDate: string
    dischargeDate: string
    activeService: boolean
}

export interface FormData {
    servedUkArmedForces: boolean | null
    servedAfter1987: boolean | null
    hearingLossOrTinnitus: boolean | null
    title: Title
    firstname: string
    lastname: string
    dob: string
    serviceNumber: string
    nationalInsurance: string
    services: ServiceRecord[]
    activeService: boolean
    leftServiceYear: number | null
    email: string
    telephone: string
    address1: string
    address2: string
    city: string
    postcode: string
    country: string
    consentToContactFuture: boolean
    consentToContactAboutThisClaim: boolean
    /** PNG data URL from the lead-form signature pad (DEV-171). */
    sigData: string
    signatureConsentChecked: boolean
}

let serviceIdSeq = 0
export const emptyService = (serviceNumber = ''): ServiceRecord => ({
    id: `svc-${serviceIdSeq++}`,
    serviceNumber,
    branch: '',
    enlistmentDate: '',
    dischargeDate: '',
    activeService: false,
})

export const initialFormData: FormData = {
    servedUkArmedForces: null,
    servedAfter1987: null,
    hearingLossOrTinnitus: null,
    title: '',
    firstname: '',
    lastname: '',
    dob: '',
    serviceNumber: '',
    nationalInsurance: '',
    services: [emptyService()],
    activeService: false,
    leftServiceYear: null,
    email: '',
    telephone: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    country: 'GB',
    consentToContactFuture: false,
    consentToContactAboutThisClaim: true,
    sigData: '',
    signatureConsentChecked: false,
}

const yearFromIso = (iso: string): number | null => {
    if (!iso) return null
    const year = parseInt(iso.slice(0, 4), 10)
    return Number.isFinite(year) ? year : null
}

const loadFieldSavers = () => require('./formDataUtils/fieldSavers')
const loadApiClient = () => require('./formDataUtils/apiClient')

const deriveLeadServiceFields = (formData: FormData) => {
    const services = formData.services || []
    const first = services[0]
    const anyActive = services.some((s) => s.activeService)
    const discharges = services
        .filter((s) => !s.activeService && s.dischargeDate)
        .map((s) => s.dischargeDate)
        .sort()
    const lastDischarge = discharges[discharges.length - 1] || ''
    // Signature fields are not part of the classic lead schema — attached separately
    const { sigData: _sigData, signatureConsentChecked: _consent, ...leadFields } = formData
    return {
        ...leadFields,
        // Validated trimmed on the frontend, and the backend checks these raw
        // (e.g. a name must start with a letter, so a stray leading space fails)
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        email: formData.email.trim(),
        telephone: formData.telephone.trim(),
        address1: formData.address1.trim(),
        address2: formData.address2.trim(),
        city: formData.city.trim(),
        postcode: formData.postcode.trim(),
        // The claim-level service number is the first service record's (each record asks for its own)
        serviceNumber: (first?.serviceNumber || '').trim(),
        services: services.map(({ id: _id, ...rest }) => ({
            ...rest,
            serviceNumber: rest.serviceNumber.trim(),
        })),
        activeService: anyActive,
        leftServiceYear: anyActive ? null : yearFromIso(lastDischarge),
        serviceBranch: first?.branch || '',
        enlistmentDate: first?.enlistmentDate || '',
        dischargeDate: first?.activeService ? '' : first?.dischargeDate || '',
    }
}

/** Stable key of lead fields that affect the unsigned PDF pack preview. */
export const getLeadPreviewFingerprint = (formData: FormData): string => {
    const lead = deriveLeadServiceFields(formData)
    const {
        // Marketing / eligibility / signature do not change populated pack content
        consentToContactFuture: _c1,
        consentToContactAboutThisClaim: _c2,
        servedUkArmedForces: _e1,
        servedAfter1987: _e2,
        hearingLossOrTinnitus: _e3,
        ...pdfFields
    } = lead
    return JSON.stringify(pdfFields)
}

export const useFormData = () => {
    const [formData, setFormData] = useState<FormData>(initialFormData)
    const [showDateErrors, setShowDateErrors] = useState(false)
    const [showEmailErrors, setShowEmailErrors] = useState(false)
    const [showPhoneErrors, setShowPhoneErrors] = useState(false)
    const [showServiceDateErrors, setShowServiceDateErrors] = useState(false)
    const [showServiceNumberErrors, setShowServiceNumberErrors] = useState(false)

    const handleFieldChange = (field: keyof FormData, value: string) => {
        setFormData((prev: FormData) => {
            if (field === 'leftServiceYear') {
                return { ...prev, leftServiceYear: value === '' ? null : parseInt(value, 10) }
            }
            if (field === 'activeService' || field === 'consentToContactFuture') {
                return { ...prev, [field]: value === 'true' }
            }
            if (
                field === 'servedUkArmedForces' ||
                field === 'servedAfter1987' ||
                field === 'hearingLossOrTinnitus'
            ) {
                return { ...prev, [field]: value === 'yes' }
            }
            if (field === 'services') {
                return prev
            }
            return { ...prev, [field]: value }
        })
    }

    const setServiceField = <K extends keyof ServiceRecord>(index: number, field: K, value: ServiceRecord[K]) => {
        setFormData((prev) => {
            const services = prev.services.map((service, i) => {
                if (i !== index) return service
                const next = { ...service, [field]: value }
                if (field === 'activeService' && value === true) {
                    next.dischargeDate = ''
                }
                return next
            })
            return { ...prev, services }
        })
    }

    const addService = () => {
        setFormData((prev) => {
            if (prev.services.length >= 3) return prev
            return { ...prev, services: [...prev.services, emptyService(prev.serviceNumber)] }
        })
    }

    const removeService = (index: number) => {
        setFormData((prev) => {
            if (prev.services.length <= 1) return prev
            return { ...prev, services: prev.services.filter((_, i) => i !== index) }
        })
    }

    const triggerDateErrors = () => setShowDateErrors(true)
    const triggerEmailErrors = () => setShowEmailErrors(true)
    const triggerPhoneErrors = () => setShowPhoneErrors(true)
    const triggerServiceDateErrors = () => setShowServiceDateErrors(true)
    const triggerServiceNumberErrors = () => setShowServiceNumberErrors(true)

    const resetFormData = () => {
        serviceIdSeq = 0
        setFormData({ ...initialFormData, services: [emptyService()] })
        setShowDateErrors(false)
        setShowEmailErrors(false)
        setShowPhoneErrors(false)
        setShowServiceDateErrors(false)
        setShowServiceNumberErrors(false)
    }

    const saveDateOfBirth = (obj: { field: string; value: string }) => {
        const { saveDateOfBirth: fn } = loadFieldSavers()
        return fn(obj, handleFieldChange)
    }

    const saveEmail = (obj: { field: string; value: string }) => {
        const { saveEmail: fn } = loadFieldSavers()
        return fn(obj, handleFieldChange)
    }

    const savePhone = (obj: { field: string; value: string }) => {
        const { savePhone: fn } = loadFieldSavers()
        return fn(obj, handleFieldChange)
    }

    const saveAddress = (obj: { field: string; value: string }) => {
        const { saveAddress: fn } = loadFieldSavers()
        return fn(obj, handleFieldChange)
    }

    const setSigData = (sigData: string) => {
        setFormData((prev) => ({ ...prev, sigData }))
    }

    const setSignatureConsentChecked = (signatureConsentChecked: boolean) => {
        setFormData((prev) => ({ ...prev, signatureConsentChecked }))
    }

    const submitFormData = useCallback(
        async (url?: string) => {
            const { submitFormData: fn } = loadApiClient()
            const lead = { ...deriveLeadServiceFields(formData), ...getAttribution() }
            const signature = buildLeadSignaturePayload(formData.sigData, formData.signatureConsentChecked)
            const payload = signature ? { ...lead, ...signature } : lead
            return fn(payload, url)
        },
        [formData]
    )

    const previewFormDocuments = useCallback(async (url?: string) => {
        const { previewFormDocuments: fn } = loadApiClient()
        // Same lead fields as submit, without signature / attribution noise
        return fn(deriveLeadServiceFields(formData), url)
    }, [formData])

    return {
        formData,
        handleFieldChange,
        setServiceField,
        setSigData,
        setSignatureConsentChecked,
        addService,
        removeService,
        saveDateOfBirth,
        showDateErrors,
        triggerDateErrors,
        showServiceDateErrors,
        triggerServiceDateErrors,
        saveEmail,
        showEmailErrors,
        triggerEmailErrors,
        savePhone,
        showPhoneErrors,
        triggerPhoneErrors,
        saveAddress,
        showServiceNumberErrors,
        triggerServiceNumberErrors,
        resetFormData,
        submitFormData,
        previewFormDocuments,
    }
}
